<template>
    <div class="animated fadeIn">
        <div class="row">
            <div class="col-12">
                <OverviewCard
                    name="Services"
                    :hasButtonAction="true"
                    :keywords="keywords"
                    :search="search"
                    @keywordsChange="setKeywords"
                    @clickAction="handleClickAddNewService"
                >
                    <table class="table table-bordered table-striped table-sm">
                        <thead>
                            <tr>
                                <th v-for="(col, key) in columns" :key="key">{{ $t(col) }}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(record, key) in records" :key="key">
                                <td>{{ record.service_name }}</td>
                                <td class="text-center">
                                    <router-link
                                        :to="'/services/' + record.service_id + '/commands'"
                                        class="mr-2"
                                    >
                                        <font-awesome-icon icon="cog" />
                                    </router-link>
                                    <button
                                        class="btn btn-link p-0 m-0 mr-2"
                                        @click="editService(key)"
                                    >
                                        <font-awesome-icon icon="edit" />
                                    </button>
                                    <button
                                        class="btn btn-link p-0 m-0 mr-2"
                                        @click="deleteService(record, true)"
                                    >
                                        <font-awesome-icon icon="trash" />
                                    </button>
                                </td>
                            </tr>
                            <tr v-if="!records.length" v-cloak>
                                <td colspan="4">
                                    <div class="text-center">{{ $t('no data') }}!</div>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                    <pagination :dataSet="dataSet" @updated="paginateTo"></pagination>

                    <hr />
                    <div slot="header">{{ $t('Clone Services') }}</div>
                    <tooltip label="Clone a Public Service">
                        <b-form-group>
                            <b-input-group>
                                <b-input-group-prepend>
                                    <b-button @click="cloneService('public')" variant="primary">
                                        <font-awesome-icon icon="clone" />
                                        {{ $t('Clone') }}
                                    </b-button>
                                </b-input-group-prepend>
                                <select class="form-control" v-model="clonePublicServiceId">
                                    <option value="0">{{ $t('None') }}</option>
                                    <option
                                        v-for="(serv, key) in publicServices"
                                        :key="key"
                                        :value="serv.service_id"
                                    >{{ serv.service_name }}</option>
                                </select>
                            </b-input-group>
                        </b-form-group>
                    </tooltip>
                    <tooltip label="Clone a Private Service">
                        <b-form-group>
                            <b-input-group>
                                <b-input-group-prepend>
                                    <b-button @click="cloneService('private')" variant="primary">
                                        <font-awesome-icon icon="clone" />
                                        {{ $t('Clone') }}
                                    </b-button>
                                </b-input-group-prepend>
                                <select class="form-control" v-model="clonePrivateServiceId">
                                    <option value="0">{{ $t('None') }}</option>
                                    <option
                                        v-for="(serv, key) in privateServices"
                                        :key="key"
                                        :value="serv.service_id"
                                    >{{ serv.service_name }}</option>
                                </select>
                            </b-input-group>
                        </b-form-group>
                    </tooltip>
                </OverviewCard>
            </div>
            <!-- /.col -->
        </div>
        <!-- /.row -->

        <b-modal
            title="Modal title"
            class="modal-primary"
            v-model="deleteModal"
            @ok="deleteService(0, false)"
        >{{ $t('Do you really need to delete') }} {{ activeElementName }} ?</b-modal>

        <b-modal
            size="lg"
            ref="serviceModal"
            :title="$t((service.mode == 'add' ? 'New' : 'Edit') + ' service')"
            :no-close-on-backdrop="true"
            hide-footer
            @ok="handleModalAction('ok')"
            @cancel="handleModalAction('cancel')"
            @hide="handleModalAction('hide')"
        >
            <div class="row">
                <div class="col-sm-12">
                    <tooltip label="service_name" placement="right" disabled>
                        <input
                            name="service_name"
                            :placeholder="$t('service_name')"
                            v-model="service.service_name"
                            v-validate="'required'"
                            :class="{'form-control': true, 'is-invalid': errors.has('service_name')}"
                        />
                        <span
                            v-show="errors.has('service_name')"
                            class="help is-danger invalid-feedback"
                        >{{ $t(errors.first('service_name')) }}</span>
                    </tooltip>

                    <tooltip label="service_description" placement="right" disabled>
                        <textarea
                            style="resize: vertical;"
                            rows="3"
                            name="service_description"
                            v-model="service.service_description"
                            v-validate="''"
                            :placeholder="$t('service_description')"
                            :class="{'form-control': true, 'is-invalid': errors.has('service_description')}"
                        ></textarea>
                        <span
                            v-show="errors.has('service_description')"
                            class="help is-danger invalid-feedback"
                        >{{ $t(errors.first('service_description')) }}</span>
                    </tooltip>

                    <tooltip label="service_status" disabled placement="right">
                        <div class="d-b">
                            <c-switch
                                class="m-0"
                                type="icon"
                                size="sm"
                                variant="success"
                                v-bind="{on: '\uf00c', off: '\uf00d'}"
                                :pill="true"
                                @change="service.service_status = !service.service_status"
                                :checked="!!service.service_status"
                            />
                        </div>
                    </tooltip>

                    <div class="form-actions">
                        <button
                            type="button"
                            @click="handleModalAction(service.mode)"
                            class="btn btn-primary btn-sm"
                        >
                            <font-awesome-icon icon="save" />
                            {{ $t(service.mode =='add' ? 'save' : 'save change') }}
                        </button>
                        <button
                            type="button"
                            @click="handleModalAction('cancel')"
                            class="btn btn-secondary btn-sm"
                        >
                            <font-awesome-icon icon="ban" />
                            {{ $t('cancel') }}
                        </button>
                    </div>
                </div>
            </div>
        </b-modal>
    </div>
</template>

<script>
import {cSwitch, OverviewCard} from '../../components'
import searchMixins from '../../mixins/search'
import axios from 'axios'

export default {
	name: 'serviceList',
	mixins: [searchMixins],
	components: {
		cSwitch,
		OverviewCard,
	},
	data() {
		return {
			dataSet: false,
			records: [],
			columns: ['service_name', 'action'],
			page: 1,
			deleteModal: false,
			activeElementId: 0,
			activeElementName: '',
			clonePublicServiceId: 0,
			clonePrivateServiceId: 0,
			publicServices: [],
			privateServices: [],
			service: {
				service_id: 0,
				service_name: '',
				service_description: '',
				service_status: 1,
				mode: 'add',
			},
		}
	},
	methods: {
		handleClickAddNewService(mode = 'show') {
			this.service = {
				service_id: 0,
				service_name: '',
				service_description: '',
				service_status: 1,
				mode: 'add',
			}
			this.$refs.serviceModal[mode]()
			this.$nextTick(() => {
				this.$validator.reset()
			})
		},

		editService(index) {
			var service = this.records[index]
			if (service) {
				this.service = {
					...service,
					mode: 'edit',
					posation: index,
				}
				this.$refs.serviceModal.show()

				this.$nextTick(() => {
					this.$validator.reset()
				})
			}
		},

		save() {
			return new Promise((resolve, reject) => {
				this.$validator.validateAll(this.columns).then((success) => {
					if (success) {
						return axios
							.post('/service/save', this.service)
							.then((response) => {
								if (response.data.status == 1) {
									if (parseInt(this.service.service_id) > 0) {
										if (this.records[this.service.posation]) {
											this.records[this.service.posation] = {...this.service}
											this.$store.commit('update_go_services', [
												this.service.service_id,
												this.service.service_name,
											])
										}
									} else {
										this.records.push({...this.service, service_id: response.data.id})
										this.$store.commit('add_go_services', {
											service_id: response.data.id,
											service_name: this.service.service_name,
										})
									}

									this.$store.commit('update_breadcrumb_history', {
										mode: 'services',
										id: response.data.id,
										change_to: this.service.service_name,
									})

									this.fetchCloneServicesData()

									return resolve(true)
								}
								return reject(response.data.msg)
							})
							.catch((e) => {
								try {
									const error = Object.assign({}, e)
									return reject(error.response.data.errors)
								} catch (err) {
									return reject(e.message)
								}
							})
					}
					reject(false)
				})
			})
		},

		handleModalAction(mode) {
			switch (mode) {
				case 'add':
				case 'edit':
					this.save()
						.then((res) => {
							this.handleClickAddNewService('hide')
						})
						.catch((err) => {
							if (is_str(err)) {
								this.errors.add({
									field: 'service_name',
									msg: err,
								})
							} else if (is_obj(err)) {
								for (var key in err) {
									this.errors.add({
										field: key,
										msg: is_str(err[key])
											? err[key]
											: is_arr(err[key]) && err[key].length
											? err[key][0]
											: 'unknown error',
									})
								}
							}
						})
					break
				case 'cancel':
					this.$refs.serviceModal.hide()
					break
				case 'hide':
					this.service = {
						service_id: 0,
						service_name: '',
						service_description: '',
						service_status: 1,
						mode: 'add',
					}
					this.$nextTick(() => {
						this.$validator.reset()
					})
					break
			}
		},

		paginateTo(page=1) {
			this.page = page
			this.records = []
			this.dataSet = false
			this.$root.loader = true
			const url = `/service?${this.keywords.length ? 'search=' + this.keywords + '&' : ''}page=${page}`
			axios.get(url).then((response) => {
				this.$root.loader = false
				if (response.data.status == 1) {
					this.dataSet = response.data.data
					this.records = response.data.data.data
				}
			})
		},
		search() {
			this.paginateTo(1)
		},

		cloneService(type) {
			var service_id = parseInt(type == 'public' ? this.clonePublicServiceId : this.clonePrivateServiceId)
			if (service_id) {
				this.$root.loader = true
				axios.post('/service/clone/' + service_id).then((response) => {
					this.$root.loader = false
					if (response.data.status == 1) {
						this.records.push(response.data.data)
						this.$root.notify({
							message: this.$t('successfully cloned'),
							status: 'success',
							area: 'topRight',
						})
						this.$store.commit('add_go_services', {
							service_id: response.data.data.service_id,
							service_name: response.data.data.service_name,
						})
					} else {
						this.$root.notify({
							message: this.$t('unknown error'),
							status: 'error',
							autoHide: false,
							area: 'topRight',
						})
					}
				})
			}
		},

		deleteService(element, alarm) {
			if (alarm) {
				this.deleteModal = true
				this.activeElementId = element.service_id
				this.activeElementName = element.service_name
			} else {
				if (this.activeElementId > 0) {
					this.$root.loader = true
					axios.post('/service/delete/' + this.activeElementId, {}).then((response) => {
						if (response.data.status == 1) {
							this.deleteModal = false
							this.paginateTo(this.page)
							this.$store.commit('delete_go_services', this.activeElementId)
							this.fetchCloneServicesData()
						} else {
							this.$root.notify({
								message: response.data.msg,
								status: 'error',
								area: 'topRight',
							})
						}
					})
				}
			}
		},

		fetchCloneServicesData() {
			this.clonePublicServiceId = this.clonePrivateServiceId = 0
			axios.get('/service/get_list').then(({data}) => {
				if (data.status) {
					this.privateServices = data.private
					this.publicServices = data.public
				}
			})
		},
	},

	mounted() {
		this.fetchCloneServicesData()
		var params = getUrlParams()
		params && params.page && Number.isInteger(parseInt(params.page)) ? params.page : 1
		this.paginateTo(params && params.page && Number.isInteger(parseInt(params.page)) ? params.page : 1)
	},
}
</script>
